/**
 * Get the site URL for redirects (auth, billing, etc.)
 * Client: always window.location.origin — keeps OAuth redirects on the same host
 * the user started on (branch URL vs per-commit deploy URL cookie mismatch).
 * Server: NEXT_PUBLIC_SITE_URL > NEXT_PUBLIC_VERCEL_URL > localhost.
 *
 * NOTE: Server-side, `NEXT_PUBLIC_VERCEL_URL` is the per-deploy URL on Vercel
 * previews (e.g. `app-abc123-...vercel.app`), NOT the stable branch URL. For
 * route handlers that have access to the inbound Request, prefer
 * `getRequestOrigin(request)` instead — it derives the origin from the host
 * the user actually hit, which avoids the per-deploy/branch URL mismatch.
 */
export function getSiteURL(): string {
  if (typeof window !== 'undefined') {
    return window.location.origin
  }

  if (process.env.NEXT_PUBLIC_SITE_URL) {
    return process.env.NEXT_PUBLIC_SITE_URL
  }

  if (process.env.NEXT_PUBLIC_VERCEL_URL) {
    return `https://${process.env.NEXT_PUBLIC_VERCEL_URL}`
  }

  return 'http://localhost:3000'
}

/**
 * Compute the public-facing origin from an inbound Request — the canonical
 * Supabase Auth callback pattern. Use this in route handlers that build absolute
 * URLs for redirects (OAuth callbacks, Stripe return URLs, etc.) so the host
 * the user actually hit is preserved.
 *
 * Behind Vercel's edge, `request.url` can occasionally carry the deployment
 * URL while `x-forwarded-host` carries the user-facing alias (custom domain or
 * stable branch URL). Prefer the forwarded host in production; fall through to
 * `request.url` in dev (where there's no edge proxy).
 *
 * Source: Supabase Server-Side Auth quickstart for Next.js.
 */
export function getRequestOrigin(request: Request): string {
  const { origin } = new URL(request.url)
  if (process.env.NODE_ENV === 'development') return origin
  const forwardedHost = request.headers.get('x-forwarded-host')
  return forwardedHost ? `https://${forwardedHost}` : origin
}
